import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiChatApi } from './aiChat.api';
import type { AiChatContext } from './aiChat.types';

export const aiChatQueryKeys = {
  all: ['ai-chat'] as const,
  bootstrap: () => [...aiChatQueryKeys.all, 'bootstrap'] as const,
  threads: () => [...aiChatQueryKeys.all, 'threads'] as const,
  messages: (threadId: string) => [...aiChatQueryKeys.all, 'messages', threadId] as const,
  approvals: () => [...aiChatQueryKeys.all, 'approvals'] as const,
};

export function useAiChatBootstrap(enabled: boolean) {
  return useQuery({
    queryKey: aiChatQueryKeys.bootstrap(),
    enabled,
    queryFn: async () => {
      const result = await aiChatApi.bootstrap();
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load AI chat');
      return result.data;
    },
  });
}

export function useAiChatThreads(enabled: boolean) {
  return useQuery({
    queryKey: aiChatQueryKeys.threads(),
    enabled,
    queryFn: async () => {
      const result = await aiChatApi.listThreads();
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load threads');
      return result.data;
    },
  });
}

export function useAiChatMessages(threadId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: aiChatQueryKeys.messages(threadId ?? 'none'),
    enabled: enabled && Boolean(threadId),
    queryFn: async () => {
      const result = await aiChatApi.listMessages(threadId!, { limit: 80 });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load messages');
      return result.data;
    },
  });
}

export function useAiChatApprovals(enabled: boolean) {
  return useQuery({
    queryKey: aiChatQueryKeys.approvals(),
    enabled,
    refetchInterval: enabled ? 15000 : false,
    queryFn: async () => {
      const result = await aiChatApi.listApprovals();
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load approvals');
      return result.data;
    },
  });
}

export function useAiChatMutations() {
  const queryClient = useQueryClient();

  const invalidateChat = async (threadId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: aiChatQueryKeys.threads() }),
      queryClient.invalidateQueries({ queryKey: aiChatQueryKeys.approvals() }),
      queryClient.invalidateQueries({ queryKey: aiChatQueryKeys.bootstrap() }),
      threadId
        ? queryClient.invalidateQueries({ queryKey: aiChatQueryKeys.messages(threadId) })
        : Promise.resolve(),
    ]);
  };

  const createThread = useMutation({
    mutationFn: async (title?: string) => {
      const result = await aiChatApi.createThread(title ? { title } : {});
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to create thread');
      return result.data;
    },
    onSuccess: async () => {
      await invalidateChat();
    },
  });

  const sendMessage = useMutation({
    mutationFn: async (input: {
      threadId: string;
      content: string;
      attachmentIds?: string[];
      speakReply?: boolean;
      inputModality?: 'TEXT' | 'VOICE';
      context?: AiChatContext;
    }) => {
      const result = await aiChatApi.sendMessage(input.threadId, {
        content: input.content,
        attachmentIds: input.attachmentIds,
        speakReply: input.speakReply,
        inputModality: input.inputModality,
        ...(input.context ? { context: input.context } : {}),
      });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to send message');
      return result.data;
    },
    onSuccess: async (_data, variables) => {
      await invalidateChat(variables.threadId);
    },
  });

  const generateImage = useMutation({
    mutationFn: async (input: { threadId: string; prompt: string }) => {
      const result = await aiChatApi.generateImage(input);
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to generate image');
      return result.data;
    },
    onSuccess: async (_data, variables) => {
      await invalidateChat(variables.threadId);
    },
  });

  const approveToolCall = useMutation({
    mutationFn: async (toolCallId: string) => {
      const result = await aiChatApi.approveToolCall(toolCallId);
      if (!result.ok || !result.data)
        throw new Error(result.error ?? 'Unable to approve tool call');
      return result.data;
    },
    onSuccess: async () => {
      await invalidateChat();
    },
  });

  const denyToolCall = useMutation({
    mutationFn: async (toolCallId: string) => {
      const result = await aiChatApi.denyToolCall(toolCallId);
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to deny tool call');
      return result.data;
    },
    onSuccess: async () => {
      await invalidateChat();
    },
  });

  const updateThread = useMutation({
    mutationFn: async (input: {
      threadId: string;
      title?: string;
      pinned?: boolean;
      voiceAutoPlay?: boolean;
    }) => {
      const { threadId, ...body } = input;
      const result = await aiChatApi.updateThread(threadId, body);
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to update thread');
      return result.data;
    },
    onSuccess: async () => {
      await invalidateChat();
    },
  });

  return {
    createThread,
    sendMessage,
    generateImage,
    approveToolCall,
    denyToolCall,
    updateThread,
    invalidateChat,
  };
}
